import { memo, useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/ContentStores/ManagedStores/ManagedStore'
import { useManagedStoreUpdateMutation } from 'hooks/mutations/content'
import {
  managedStoreConfigRoute,
  managedStoreRoute,
} from 'pages/NWPlatform/ContentStores/ManagedStores/ManagedStore/Routing'
import { managedStoreQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['entity', 'pages'])
  const { enqueueSnackbar } = useSnackbar()

  const { managedStoreId } = managedStoreRoute.useParams()

  const { data, isLoading, isPending } = useQuery(managedStoreQueryKeys.detail({ storeId: managedStoreId }))

  const { mutate: updateManagedStore } = useManagedStoreUpdateMutation()

  const handleUpdateManagedStoreName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateManagedStore(
        {
          storeId: managedStoreId,
          contentManagedStoreEdit: {
            ...data,
            name: title,
            ingestionEndpoints: data?.ingestionEndpoints ?? [],
          },
        },
        {
          onError: () => enqueueSnackbar(t('pages:ContentStores.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [updateManagedStore, managedStoreId, data, enqueueSnackbar, t],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={data?.name ?? ''}
          isLoading={isLoading}
          isSaving={isPending}
          onSave={handleUpdateManagedStoreName}
          Actions={<ActionsMenu />}
        />
      }
      SubNavigation={
        <Tabs>
          <NavigationTabLink from={managedStoreRoute.to} to={managedStoreConfigRoute.to}>
            {t('pages:ContentStores.config')}
          </NavigationTabLink>
        </Tabs>
      }
    />
  )
}

export const Structure = memo(StructureRaw)
